/**
 * Every word of the first-run tour (#776), in one place so it can be reviewed and edited without touching code.
 * Each step: a short title and 1-3 short paragraphs. Keep paragraphs under ~200 characters so a step fits a phone
 * bottom sheet without scrolling.
 *
 * The step order, the element each step points at and the page it opens live in steps.ts; the keys here must match
 * the step ids there (a unit test checks this).
 */
export const TOUR_COPY = {
  welcome: {
    title: 'Welcome to outcomes.lol',
    body: [
      'outcomes.lol is a frontend for outcome markets on Hyperliquid (HIP-4): questions about real-world events that you trade by buying Yes or No shares.',
      'This short tour shows you around. Nothing here places an order or opens your wallet. Skip it any time with Esc.',
    ],
  },
  env: {
    title: 'Testnet or mainnet',
    body: [
      'This switch picks the Hyperliquid network. Markets, balances and prices differ between the two.',
      'Trading is testnet-only for now: testnet uses free test USDC, so you can practise without risking money. Mainnet is view-only.',
    ],
  },
  catalog: {
    title: 'The market list',
    body: [
      'Each card is a question about an event. Related outcomes are grouped under the event they belong to.',
      'The price next to each outcome is the live Yes price. Open any of them to see its order book and trade.',
    ],
  },
  catalogControls: {
    title: 'Search and sort',
    body: [
      'Search by name, filter by venue and sort the list by newest, Yes price or name.',
      'Switch to "All outcomes" for one flat list instead of grouped questions.',
    ],
  },
  marketSides: {
    title: 'Yes and No',
    body: [
      'Every outcome has two sides. A Yes share pays 1 USDC if the outcome happens, a No share pays 1 USDC if it does not.',
      'So the price is the market’s probability: Yes at 63¢ means traders think it is about 63% likely.',
    ],
  },
  marketBook: {
    title: 'The order book',
    body: [
      'Prices are in cents per share (1¢ to 99¢). Asks (sellers) sit on top, bids (buyers) below, with the spread in the middle.',
      'Tap a price level to copy it into the order ticket.',
    ],
  },
  marketTrades: {
    title: 'Recent trades',
    body: ['The latest fills on this side, newest first: time, price and shares. Green was a buy, red a sell.'],
  },
  ticket: {
    title: 'The order ticket',
    body: [
      'Pick Yes or No, then Buy to open a position or Sell to close shares you already hold. Sells never open a short.',
    ],
  },
  ticketPrice: {
    title: 'Limit, Market and time in force',
    body: [
      'A limit order sets the price you are willing to pay. Market fills now against the book, up to a worst-case price shown before you sign.',
      'Time in force decides what happens to the unfilled part: rest on the book (GTC), fill what it can now and cancel the rest (IOC), or only add liquidity (ALO, post-only).',
    ],
  },
  ticketEconomics: {
    title: 'What it costs and pays',
    body: [
      'This box shows the cost, the payout if you are right and your potential profit, plus your USDC and shares.',
      'Orders must be worth at least 1 USDC (price × shares).',
    ],
  },
  wallet: {
    title: 'Your wallet',
    body: [
      'Connect a browser wallet to trade. Every order and cancel is signed in your wallet: outcomes.lol never holds your keys or funds.',
      'Optional 1-click trading approves a browser-held trading key once, so orders no longer need a wallet pop-up. You can turn it off any time.',
    ],
  },
  portfolio: {
    title: 'Portfolio and ghost mode',
    body: [
      'Portfolio shows balances, positions, open orders, order history and trades.',
      'Ghost mode lets you watch any address read-only, like the public trader shown here. Exit it from the GHOST chip in the header.',
    ],
  },
  finish: {
    title: 'You are ready',
    body: [
      'Start on testnet, try a small order and watch it in your portfolio.',
      'You can replay this tour any time from the Help menu.',
    ],
  },
} as const satisfies Record<string, { title: string; body: readonly string[] }>;

export type TourCopyKey = keyof typeof TOUR_COPY;

/** Button labels and other chrome. */
export const TOUR_UI = {
  next: 'Next',
  back: 'Back',
  skip: 'Skip tour',
  finish: 'Done',
  stepOf: (n: number, total: number) => `Step ${n} of ${total}`,
  learn: 'Learn how prediction contracts work',
  helpMenu: 'Help',
  replay: 'Replay tour',
  tourLabel: 'outcomes.lol tour',
  keysHint: 'Esc to skip · ← → to move',
} as const;
